import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ContractFormFields } from 'page/CustomerPage/types/contractFormField';
import {
  ContractEditStep,
  CONTRACT_EDIT_STEP,
} from 'page/CustomerPage/types/contractEditStep';

import type { RootState } from 'store';
import { convertUTCStringIntoJSTString, DATE_FORMAT } from 'utils/dateUtils';

/**
 * 契約情報の編集画面の状態
 */
interface ContractEditState {
  /**
   * 編集モーダル表示フラグ
   */
  isOpen: boolean;
  /**
   * 編集のステップ
   */
  step: ContractEditStep;
  /**
   * 契約メニュー ID
   */
  menuId: number | undefined;
  /**
   * 有効/無効
   */
  valid: boolean;
  /**
   * 契約開始日
   */
  startDate: string;
  /**
   * 契約終了日
   */
  endDate: string;
}

/**
 * 契約情報編集画面の初期状態
 */
const initialState: ContractEditState = {
  isOpen: false,
  step: CONTRACT_EDIT_STEP.INPUT,
  menuId: undefined,
  valid: true,
  startDate: '',
  endDate: '',
};

/**
 * 顧客情報編集画面の slice(reducer + action)
 */
export const contractEditSlice = createSlice({
  name: 'contractEdit',
  initialState,
  reducers: {
    init: () => initialState,
    modalOpened: (state) => {
      state.isOpen = true;
    },
    stepMovedToConfirmation: (state) => {
      state.step = CONTRACT_EDIT_STEP.CONFIRMATION;
    },
    stepMovedToInput: (state) => {
      state.step = CONTRACT_EDIT_STEP.INPUT;
    },
    stepMovedToDelete: (state) => {
      state.step = CONTRACT_EDIT_STEP.DELETE;
    },
    formGroupUpdated: (
      state,
      action: PayloadAction<{
        form: ContractFormFields;
      }>,
    ) => {
      const { menuId } = action.payload.form;
      state.menuId = menuId ? parseInt(menuId, 10) : undefined;
      state.valid = action.payload.form.valid;
    },
    startDateChanged: (
      state,
      action: PayloadAction<{
        startDate: string;
      }>,
    ) => {
      state.startDate = action.payload.startDate;
    },
    endDateChanged: (
      state,
      action: PayloadAction<{
        endDate: string;
      }>,
    ) => {
      state.endDate = action.payload.endDate;
    },
  },
});

/**
 * 契約情報編集画面の action
 */
export const contractEditActions = contractEditSlice.actions;

/**
 * 契約情報編集画面の selectors
 */
export const contractEditSelectors = {
  isOpenInputModal: (state: RootState) =>
    state.customer.contractEdit.isOpen &&
    state.customer.contractEdit.step === CONTRACT_EDIT_STEP.INPUT,
  isOpenConfirmationModal: (state: RootState) =>
    state.customer.contractEdit.isOpen &&
    state.customer.contractEdit.step === CONTRACT_EDIT_STEP.CONFIRMATION,
  isOpenDeleteModal: (state: RootState) =>
    state.customer.contractEdit.isOpen &&
    state.customer.contractEdit.step === CONTRACT_EDIT_STEP.DELETE,
  menuId: (state: RootState) => state.customer.contractEdit.menuId,
  valid: (state: RootState) => state.customer.contractEdit.valid,
  displayValid: (state: RootState) =>
    state.customer.contractEdit.valid ? '有効' : '無効',
  startDate: (state: RootState) => state.customer.contractEdit.startDate,
  displayStartDate: (state: RootState) => {
    const date = state.customer.contractEdit.startDate;

    return date ? convertUTCStringIntoJSTString(date, DATE_FORMAT.DATE) : '';
  },
  endDate: (state: RootState) => state.customer.contractEdit.endDate,
  displayEndDate: (state: RootState) => {
    const date = state.customer.contractEdit.endDate;

    return date ? convertUTCStringIntoJSTString(date, DATE_FORMAT.DATE) : '';
  },
} as const;
